import type { MealPatternStatusName } from '@steado/admin-contract/meal-patterns';

import type { ToneName } from '@/components/table/tone';

/**
 * What each status means at a glance.
 *
 * Only the two live statuses are "on" colours: everything else is editorial,
 * and an operator scanning this list is asking one question — which of these
 * shapes is the generator actually enforcing right now.
 */
export const PATTERN_STATUS_TONES: Readonly<Record<MealPatternStatusName, ToneName>> = {
  draft: 'mute',
  internal_review: 'info',
  active_provisional: 'accent',
  expert_reviewed: 'ok',
  retired: 'warn',
};

/** Whether a version is one plan generation reads. */
export function isLiveStatus(status: MealPatternStatusName): boolean {
  return status === 'active_provisional' || status === 'expert_reviewed';
}

/**
 * A component list as one readable line, in the same words the model is given
 * and a violation message quotes back.
 *
 * Deliberately the console's own rendering rather than the API's: the API
 * renders this into a prompt, and a wire field carrying prose would be a
 * second place the wording could drift from the grammar it describes.
 */
export function describeComponents(
  components: readonly { role: string; required: boolean; min: number; max: number }[],
): string {
  const label = (role: string) => (role === 'any_dish' ? 'dish' : role);
  const required = components
    .filter((component) => component.required)
    .map((component) =>
      component.min === component.max
        ? `${String(component.min)} ${label(component.role)}`
        : `${String(component.min)}-${String(component.max)} ${label(component.role)}`,
    );
  const optional = components
    .filter((component) => !component.required)
    .map((component) => `up to ${String(component.max)} ${label(component.role)}`);

  if (required.length === 0) return optional.join(', ');
  const head = required.join(' + ');
  return optional.length === 0 ? head : `${head}, plus ${optional.join(', ')}`;
}
